import React from 'react';

import ProductSwitcher from '../../utils/ProductSwitcher';

/**
 * Contextual heading block for the login/signup page.
 * Adapts title, subtitle, and sub-subtitle based on whether
 * the user arrived via a claim or team-join link.
 *
 * For claims: uses template actionText.heading/subheading, or product type
 * (e.g. "Get Cashless Tips"). Never uses tag displayName.
 */
export default function LoginHeading({
  loginContext,
  isSignup,
  locStrings,
  step,
  of,
}) {
  const isContextualFlow = loginContext && (loginContext.type === 'claim' || loginContext.type === 'join');
  const hasLoadedContext = !loginContext?.loading && isContextualFlow;
  const mode = isSignup ? 'signup' : 'login';

  let titleText = locStrings.title[mode];
  let subtitleText = locStrings.subtitle[mode];
  let subsubtitleText = locStrings.subsubtitle[mode];
  // Set when the subtitle carries an action (the join landing's sign-up link).
  let subtitleNode = null;
  // Join landings draw the invite as part of the heading — the team's logo beside
  // an eyebrow line — instead of the full-bleed coloured strip the claim flow uses,
  // which repeated the team name in a block of brand pink above "Join {Team}".
  let eyebrowText = null;
  let logoUrl = null;

  // While loading join/claim context, show loading state instead of generic
  if (loginContext?.loading && isContextualFlow) {
    titleText = 'Loading…';
    subtitleText = '';
    subsubtitleText = '';
  } else if (hasLoadedContext && loginContext.type === 'claim') {
    const productMap = new ProductSwitcher({ locStrings }).getProductMap(loginContext.product);
    const heading = loginContext.actionTextHeading || productMap?.heading;
    const subheading = loginContext.actionTextSubheading || productMap?.subheading;
    titleText = heading
      ? (isSignup ? heading : `Log in — ${heading}`)
      : (isSignup ? 'Claim your tag' : 'Log in to claim your tag');
    subtitleText = subheading || (isSignup ? 'Enter your mobile number to get started.' : 'Log in to claim your tag');
    subsubtitleText = '';
  } else if (hasLoadedContext && loginContext.type === 'join') {
    // A join link reaches brand-new staff at least as often as returning users, so
    // the landing presumes neither. It used to open on "Log in to join …", which
    // read as a wall to anyone without an account. Now: new people are pointed
    // straight at sign-up, and anyone unsure is told what the email/mobile field
    // does for them (the identity lookup finds an existing account before any
    // sign-up is offered — that gate is unchanged).
    const teamName = loginContext.teamName || 'your team';
    titleText = teamName;
    eyebrowText = 'You\u2019ve been invited to join';
    logoUrl = loginContext.topLogo || null;
    // The link is an invitation, so sign-up leads: the number field sits right
    // under this heading. Returners take "Already have an account? Log in" below
    // and get the login wording here instead.
    if (isSignup) {
      subtitleText = 'Enter your mobile number to get started.';
      subsubtitleText = '';
    } else {
      subtitleText = 'Log in to accept your invite.';
      subtitleNode = null;
      subsubtitleText = '';
    }
  }

  // Default signup/login: larger display headline; claim/join: slightly smaller for long copy
  const isCompactTitle = hasLoadedContext || (isContextualFlow && loginContext?.loading);
  const titleSize = isCompactTitle
    ? 'clamp(1.65rem, 5vw, 2.1rem)'
    : 'clamp(1.95rem, 6.5vw, 2.85rem)';
  const subtitleSize = isCompactTitle
    ? 'clamp(1.05rem, 3.2vw, 1.2rem)'
    : 'clamp(1.2rem, 3.8vw, 1.45rem)';
  const subsubtitleSize = isCompactTitle
    ? 'clamp(0.95rem, 2.8vw, 1.05rem)'
    : 'clamp(1.05rem, 3.2vw, 1.2rem)';

  return (
    <div
      className="d-flex flex-column justify-content-start text-start px-0 mt-0 mb-4"
      style={{ width: '100%', gap: '0.65rem' }}
    >
      {step && of ? (
        <span
          className="text-white-50"
          style={{
            fontSize: 14, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
          }}
        >
          {`Step ${step} of ${of}`}
        </span>
      ) : null}
      {eyebrowText ? (
        // Invite header: logo (when the team has one) beside the eyebrow + team name.
        <div className="d-flex align-items-center" style={{ gap: '1rem', marginBottom: '0.35rem' }}>
          {logoUrl && (
            // Team images are whatever the venue uploaded — a square mark, a wide
            // wordmark, a photo. Fix the height only, let the width follow the image,
            // and never crop: a wide logo shows whole, a square one stays square.
            <img
              src={logoUrl}
              alt=""
              style={{
                height: 64,
                width: 'auto',
                maxWidth: 'min(45%, 180px)',
                objectFit: 'contain',
                borderRadius: 12,
                padding: 4,
                border: '1px solid rgba(255,255,255,0.18)',
                background: '#111',
                flexShrink: 0,
              }}
            />
          )}
          <div className="d-flex flex-column" style={{ gap: '0.2rem', minWidth: 0 }}>
            <span
              className="text-white-50"
              style={{
                fontSize: 'clamp(0.8rem, 2.4vw, 0.9rem)',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {eyebrowText}
            </span>
            <h1
              className="mb-0 text-white h3"
              style={{
                fontSize: titleSize,
                fontWeight: 800,
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
              }}
            >
              {titleText}
            </h1>
          </div>
        </div>
      ) : (
        <h1
          className="mb-0 text-white h3"
          style={{
            fontSize: titleSize,
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
          }}
        >
          {titleText}
        </h1>
      )}
      {subtitleText ? (
        <p
          className="mb-0 text-white h6"
          style={{
            fontSize: subtitleSize,
            fontWeight: 700,
            lineHeight: 1.35,
            letterSpacing: '-0.01em',
          }}
        >
          {subtitleNode || subtitleText}
        </p>
      ) : null}
      {subsubtitleText !== '' && (
        <p
          className="text-white-50 mb-0 body-reg"
          style={{
            fontSize: subsubtitleSize,
            fontWeight: 500,
            lineHeight: 1.45,
          }}
        >
          {subsubtitleText}
        </p>
      )}
    </div>
  );
}
